/**
 * Lock Collateral Status SignalR Module
 * Handles real-time status updates for lock product collateral (videos and documents)
 * via SignalR. Covers property video, social video, social ad video, flyer, and certificate.
 * Replaces legacy polling mechanism.
 */

class LockCollateralStatusSignalR {
    constructor(confirmationNumber, timeoutSeconds = 300) {
        this.confirmationNumber = confirmationNumber;
        this.timeoutSeconds = timeoutSeconds;
        this.connection = null;
        this.handlers = {};
        this.timeoutTimers = {};
        this.connected = false;
    }

    /**
     * Initialize SignalR connection and subscribe to collateral status updates
     */
    async initialize() {
        try {
            // Create SignalR connection
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl("/hubs/video-status")
                .withAutomaticReconnect()
                .build();

            // Handle VideoReady event from server
            this.connection.on("VideoReady", (data) => {
                console.log("SignalR VideoReady notification received:", data);
                this.handleVideoReady(data);
            });

            // Handle VideoError event from server
            this.connection.on("VideoError", (data) => {
                console.log("SignalR VideoError notification received:", data);
                this.handleVideoError(data);
            });

            // Handle DocumentReady event from server
            this.connection.on("DocumentReady", (data) => {
                console.log("SignalR DocumentReady notification received:", data);
                this.handleDocumentReady(data);
            });

            // Handle DocumentError event from server (H3)
            this.connection.on("DocumentError", (data) => {
                console.log("SignalR DocumentError notification received:", data);
                this.handleDocumentError(data);
            });

            // Handle VideoQRCodeReady event from server
            this.connection.on("VideoQRCodeReady", (data) => {
                console.log("SignalR VideoQRCodeReady notification received:", data);
                this.handleVideoQRCodeReady(data);
            });

            // Handle VideoQRCodeError event from server
            this.connection.on("VideoQRCodeError", (data) => {
                console.log("SignalR VideoQRCodeError notification received:", data);
                this.handleVideoQRCodeError(data);
            });

            // Handle connection state changes
            this.connection.onreconnecting(() => {
                console.log("SignalR connection reconnecting...");
            });

            this.connection.onreconnected(async () => {
                console.log("SignalR connection reconnected. Resubscribing...");
                await this.subscribe();

                // Phase 2 Solution 5: Fetch current collateral status after reconnection
                await this.fetchCurrentVideoStatus();
            });

            this.connection.onclose(() => {
                console.log("SignalR connection closed.");
                this.connected = false;
            });

            // Start connection
            await this.connection.start();
            console.log("SignalR connection established.");

            // Subscribe to confirmation number group
            await this.subscribe();

            // Fetch current status to catch notifications that arrived before we connected
            await this.fetchCurrentVideoStatus();

            this.connected = true;
        } catch (error) {
            console.error("SignalR connection failed:", error);
            this.connected = false;
            throw error;
        }
    }

    /**
     * Subscribe to collateral status updates for the confirmation number
     */
    async subscribe() {
        if (this.connection && this.confirmationNumber && this.connection.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("SubscribeToVideoStatus", this.confirmationNumber);
                console.log(`Subscribed to collateral status for: ${this.confirmationNumber}`);
            } catch (error) {
                console.error("Failed to subscribe to collateral status:", error);
            }
        }
    }

    /**
     * Unsubscribe from collateral status updates
     */
    async unsubscribe() {
        if (this.connection && this.confirmationNumber && this.connection.state === signalR.HubConnectionState.Connected) {
            try {
                await this.connection.invoke("UnsubscribeFromVideoStatus", this.confirmationNumber);
                console.log(`Unsubscribed from collateral status for: ${this.confirmationNumber}`);
            } catch (error) {
                console.error("Failed to unsubscribe from collateral status:", error);
            }
        }
    }

    /**
     * Register a video type handler
     * @param {string} videoType - Video type (custom, social, social_ad)
     * @param {Function} onSuccess - Callback when video is ready
     * @param {Function} onTimeout - Callback when timeout occurs
     * @param {Function} onError - Callback when error occurs
     */
    registerVideoHandler(videoType, onSuccess, onTimeout, onError) {
        this.handlers[videoType] = {
            onSuccess,
            onTimeout,
            onError
        };

        // Start timeout timer
        this.startTimeoutTimer(videoType, onTimeout);

        console.log(`Registered handler for video type: ${videoType}`);
    }

    /**
     * Register a document type handler (flyer, certificate)
     * @param {string} documentType - Document type
     * @param {Function} onSuccess - Callback when document is ready
     * @param {Function} onTimeout - Callback when timeout occurs
     * @param {Function} onError - Callback when error occurs
     */
    registerDocumentHandler(documentType, onSuccess, onTimeout, onError) {
        this.handlers[documentType] = {
            onSuccess,
            onTimeout,
            onError
        };

        // Start timeout timer
        this.startTimeoutTimer(documentType, onTimeout);

        console.log(`Registered handler for document type: ${documentType}`);
    }

    /**
     * Start timeout timer for a video or document type
     */
    startTimeoutTimer(handlerKey, onTimeout) {
        if (this.timeoutTimers[handlerKey]) {
            clearTimeout(this.timeoutTimers[handlerKey]);
        }

        this.timeoutTimers[handlerKey] = setTimeout(() => {
            console.log(`Timeout reached for: ${handlerKey}`);
            if (onTimeout) {
                onTimeout(handlerKey);
            }
            delete this.handlers[handlerKey];
        }, this.timeoutSeconds * 1000);
    }

    /**
     * Handle VideoReady notification from SignalR
     */
    handleVideoReady(data) {
        const { confirmationNumber, videoType, videoUrl, thumbnailUrl, downloadUrl, qrCodeUrl } = data;

        // Verify this notification is for our confirmation number
        if (confirmationNumber !== this.confirmationNumber) {
            console.warn(`Received notification for different confirmation: ${confirmationNumber}`);
            return;
        }

        // Find and execute handler for this video type
        const handler = this.handlers[videoType];
        if (handler && handler.onSuccess) {
            console.log(`Executing handler for video type: ${videoType}`);
            console.log(`SignalR data includes: videoUrl=${!!videoUrl}, thumbnailUrl=${!!thumbnailUrl}, downloadUrl=${!!downloadUrl}, qrCodeUrl=${!!qrCodeUrl}`);

            // Clear timeout timer
            if (this.timeoutTimers[videoType]) {
                clearTimeout(this.timeoutTimers[videoType]);
                delete this.timeoutTimers[videoType];
            }

            // Execute success callback with all data including download URLs
            // Wrap in try-catch to preserve handler on error
            try {
                handler.onSuccess({
                    videoType,
                    videoUrl,
                    thumbnailUrl,
                    downloadUrl: downloadUrl || '',
                    qrCodeUrl: qrCodeUrl || ''
                });

                // Don't automatically delete handler - let it persist for regenerations
                // Handler will only be deleted on timeout or explicit removal
                console.log(`Handler for ${videoType} executed successfully, keeping for potential regenerations`);
            } catch (error) {
                console.error(`Error executing handler for ${videoType}:`, error);
                // Don't delete handler on error - allow retry on next notification
            }
        } else {
            console.warn(`No handler registered for video type: ${videoType}`);
        }
    }

    /**
     * Handle VideoQRCodeReady event
     */
    handleVideoQRCodeReady(data) {
        if (!data || !data.confirmationNumber || data.confirmationNumber !== this.confirmationNumber) return;

        // Map to a pseudo video object that only has the QR code
        const qrData = {
            videoUrl: 'Pending', // Video is still pending
            thumbnailUrl: 'Pending',
            qrCodeUrl: data.qrCodeUrl, // use qrCodeUrl to match onSuccess expected field
            qrCodeDownloadUrl: data.qrCodeDownloadUrl
        };

        // Reuse property video handler if registered
        const handler = this.handlers['property'];
        if (handler && handler.onSuccess) {
            console.log("Executing handler for VideoQRCodeReady (property)");
            handler.onSuccess(qrData);
        } else {
            console.warn("No handler registered for property video (VideoQRCodeReady)");
        }
    }

    /**
     * Handle VideoQRCodeError event
     */
    handleVideoQRCodeError(data) {
        if (!data || !data.confirmationNumber || data.confirmationNumber !== this.confirmationNumber) return;

        const { errorMessage } = data;

        // Reuse property video handler if registered
        const handler = this.handlers['property'];
        if (handler && handler.onError) {
            console.log("Executing error handler for VideoQRCodeError (property)");
            handler.onError({
                videoType: 'property',
                errorMessage: errorMessage,
                formattedError: `ERROR:${errorMessage}`
            });
        } else {
            console.warn("No handler registered for property video (VideoQRCodeError)");
        }
    }

    /**
     * Handle VideoError notification from SignalR
     */
    handleVideoError(data) {
        const { confirmationNumber, videoType, errorMessage } = data;

        // Verify this notification is for our confirmation number
        if (confirmationNumber !== this.confirmationNumber) {
            console.warn(`Received error notification for different confirmation: ${confirmationNumber}`);
            return;
        }

        // Find and execute error handler for this video type
        const handler = this.handlers[videoType];
        if (handler && handler.onError) {
            console.log(`Executing error handler for video type: ${videoType}`);

            // Clear timeout timer
            if (this.timeoutTimers[videoType]) {
                clearTimeout(this.timeoutTimers[videoType]);
                delete this.timeoutTimers[videoType];
            }

            // Execute error callback with formatted message
            handler.onError({
                videoType,
                errorMessage,
                // Format error message with "ERROR:" prefix for consistency with database
                formattedError: `ERROR:${errorMessage}`
            });

            // Keep handler for potential regeneration attempts
            // Restart timeout timer to allow user to fix and regenerate
            this.startTimeoutTimer(videoType, handler.onTimeout);
            console.log(`Handler for ${videoType} preserved after error, allowing regeneration`);
        } else {
            console.warn(`No error handler registered for video type: ${videoType}`);
        }
    }

    /**
     * Handle DocumentReady notification from SignalR
     */
    handleDocumentReady(data) {
        const { confirmationNumber, documentType, documentUrl } = data;

        // Verify this notification is for our confirmation number
        if (confirmationNumber !== this.confirmationNumber) {
            console.warn(`Received document notification for different confirmation: ${confirmationNumber}`);
            return;
        }

        // Find and execute handler for this document type
        const handler = this.handlers[documentType];
        if (handler && handler.onSuccess) {
            console.log(`Executing handler for document type: ${documentType}`);

            // Clear timeout timer
            if (this.timeoutTimers[documentType]) {
                clearTimeout(this.timeoutTimers[documentType]);
                delete this.timeoutTimers[documentType];
            }

            try {
                handler.onSuccess({
                    documentType,
                    documentUrl
                });
                console.log(`Handler for ${documentType} executed successfully`);
            } catch (error) {
                console.error(`Error executing handler for ${documentType}:`, error);
            }
        } else {
            console.warn(`No handler registered for document type: ${documentType}`);
        }
    }

    /**
     * Handle DocumentError notification from SignalR (H3)
     * Fires the registered onError callback immediately so the UI updates without waiting for client-side timeout.
     */
    handleDocumentError(data) {
        const { confirmationNumber, documentType, errorMessage } = data;

        // Verify this notification is for our confirmation number
        if (confirmationNumber !== this.confirmationNumber) {
            console.warn(`Received document error notification for different confirmation: ${confirmationNumber}`);
            return;
        }

        // Find and execute error handler for this document type
        const handler = this.handlers[documentType];
        if (handler && handler.onError) {
            console.log(`Executing error handler for document type: ${documentType}`);

            // Clear timeout timer — server already confirmed failure, no need to keep waiting
            if (this.timeoutTimers[documentType]) {
                clearTimeout(this.timeoutTimers[documentType]);
                delete this.timeoutTimers[documentType];
            }

            handler.onError({
                documentType,
                errorMessage
            });

            console.log(`Error handler for ${documentType} executed`);
        } else {
            console.warn(`No error handler registered for document type: ${documentType}`);
        }
    }

    /**
     * Stop SignalR connection and clean up
     */
    async stop() {
        // Clear all timeout timers
        Object.keys(this.timeoutTimers).forEach(key => {
            clearTimeout(this.timeoutTimers[key]);
        });
        this.timeoutTimers = {};
        this.handlers = {};

        // Unsubscribe and stop connection
        if (this.connection) {
            try {
                if (this.connection.state === signalR.HubConnectionState.Connected) {
                    await this.unsubscribe();
                }
                await this.connection.stop();
                console.log("SignalR connection stopped.");
            } catch (error) {
                console.error("Error stopping SignalR connection:", error);
            }
            this.connected = false;
        }
    }

    /**
     * Check if SignalR is connected
     */
    isConnected() {
        return this.connected && this.connection && this.connection.state === signalR.HubConnectionState.Connected;
    }

    /**
     * Detect the product type based on the current page URL
     * Returns the appropriate API endpoint base path
     */
    getVideoStatusEndpoint() {
        // Use window.location.pathname as the base for ALL product types.
        // This preserves the /mysite/{name}/ or /team/{name}/ prefix required by the
        // controller's class-level Route attribute. Do NOT use hardcoded paths —
        // they break for BSL (/mysite/{name}/builder-lock/...) and BFL routes.
        const basePath = window.location.pathname.replace(/\/$/, '');
        return basePath + '/get-video-status';
    }

    /**
     * Phase 2 Solution 5: Fetch current collateral status from database
     * Called after reconnection to catch any notifications that were missed
     */
    async fetchCurrentVideoStatus() {
        try {
            const endpoint = this.getVideoStatusEndpoint();
            console.log(`Fetching current collateral status for ${this.confirmationNumber} from ${endpoint}`);

            const response = await fetch(`${endpoint}?confirmationNumber=${this.confirmationNumber}`);

            if (!response.ok) {
                console.warn(`Failed to fetch collateral status: ${response.status} ${response.statusText}`);
                return;
            }

            const data = await response.json();

            // Determine if this is a builder product (uses 'custom' video type) or ListNLock (uses 'property')
            const isBuilderProduct = endpoint.includes('builder-lock') || endpoint.includes('builder-forward');

            // Trigger handlers for any ready videos
            if (data.propertyVideo && data.propertyVideo.videoUrl && data.propertyVideo.videoUrl !== 'Pending' && !data.propertyVideo.videoUrl.startsWith('ERROR:')) {
                console.log('Fetched property video status:', data.propertyVideo);

                // For builder products, also try 'custom' video type since SpecLock uses that
                // For ListNLock, use 'property' video type
                const videoTypes = isBuilderProduct ? ['custom', 'property'] : ['property'];

                videoTypes.forEach(videoType => {
                    this.handleVideoReady({
                        confirmationNumber: this.confirmationNumber,
                        videoType: videoType,
                        videoUrl: data.propertyVideo.videoUrl,
                        thumbnailUrl: data.propertyVideo.thumbnailUrl,
                        downloadUrl: data.propertyVideo.downloadUrl,
                        qrCodeUrl: data.propertyVideo.qrCodeUrl
                    });
                });
            }

            if (data.socialVideo && data.socialVideo.videoUrl && data.socialVideo.videoUrl !== 'Pending' && !data.socialVideo.videoUrl.startsWith('ERROR:')) {
                console.log('Fetched social video status:', data.socialVideo);
                this.handleVideoReady({
                    confirmationNumber: this.confirmationNumber,
                    videoType: 'social',
                    videoUrl: data.socialVideo.videoUrl,
                    thumbnailUrl: data.socialVideo.thumbnailUrl,
                    downloadUrl: data.socialVideo.downloadUrl,
                    qrCodeUrl: data.socialVideo.qrCodeUrl
                });
            }

            if (data.socialAdVideo && data.socialAdVideo.videoUrl && data.socialAdVideo.videoUrl !== 'Pending' && !data.socialAdVideo.videoUrl.startsWith('ERROR:')) {
                console.log('Fetched social ad video status:', data.socialAdVideo);
                this.handleVideoReady({
                    confirmationNumber: this.confirmationNumber,
                    videoType: 'social_ad',
                    videoUrl: data.socialAdVideo.videoUrl,
                    thumbnailUrl: data.socialAdVideo.thumbnailUrl,
                    downloadUrl: data.socialAdVideo.downloadUrl,
                    qrCodeUrl: data.socialAdVideo.qrCodeUrl
                });
            }

            // Phase 2: Handle document status updates after reconnection.
            // qrCodeUrl is included so late-connecting clients recover a QR notification that
            // fired before the page finished loading.
            if (data.qrCodeUrl) {
                console.log('Fetched QR code status:', data.qrCodeUrl);
                this.handleDocumentReady({
                    confirmationNumber: this.confirmationNumber,
                    documentType: 'qr_code',
                    documentUrl: data.qrCodeUrl
                });
            }

            if (data.flyerUrl) {
                console.log('Fetched flyer status:', data.flyerUrl);
                this.handleDocumentReady({
                    confirmationNumber: this.confirmationNumber,
                    documentType: 'flyer',
                    documentUrl: data.flyerUrl
                });
            }

            if (data.certificateUrl) {
                console.log('Fetched certificate status:', data.certificateUrl);
                this.handleDocumentReady({
                    confirmationNumber: this.confirmationNumber,
                    documentType: 'certificate',
                    documentUrl: data.certificateUrl
                });
            }
        } catch (error) {
            console.error('Failed to fetch current collateral status:', error);
        }
    }
}

export default LockCollateralStatusSignalR;
